import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { FormElement } from '../../../components/FormElement';
import { usePermissions } from '../../../components/auth/usePermissions';
import { useChangeOtherSettingsRepository } from '../../../repository/settings/useChangeOtherSettingsRepository';
import { SupportedCurrency } from '../../../domain/currency';
import { Permission } from '../../../domain/permissions';
import { OtherSettings as OtherSettingsType } from '../../../domain/otherSettings';
import { useOtherSettingsForm } from './useOtherSettingsForm';
import { useOtherSettingsChanged } from './useOtherSettingsChanged';

type OtherSettingsProps = {
    originalOtherSettings: OtherSettingsType;
}

export const OtherSettings = ({ originalOtherSettings }: OtherSettingsProps) => {
    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(Permission.SETTINGS_EDIT);
    const { otherSettings, setOtherSettings } = useOtherSettingsForm(originalOtherSettings);
    const { anyChanges } = useOtherSettingsChanged(otherSettings, originalOtherSettings);

    const { changeOtherSettings } = useChangeOtherSettingsRepository();

    const handleOtherSettingsItemChange = (change: Partial<OtherSettingsType>) => {
        setOtherSettings({
            ...otherSettings,
            ...change,
        });
    };

    const handleSubmitButtonClick = async () => {
        await changeOtherSettings(otherSettings);

        toast.success('Pomyślnie zmieniono ustawienia');
    };

    const months = [
        { value: 1, label: 'Styczeń' },
        { value: 2, label: 'Luty' },
        { value: 3, label: 'Marzec' },
        { value: 4, label: 'Kwiecień' },
        { value: 5, label: 'Maj' },
        { value: 6, label: 'Czerwiec' },
        { value: 7, label: 'Lipiec' },
        { value: 8, label: 'Sierpień' },
        { value: 9, label: 'Wrzesień' },
        { value: 10, label: 'Październik' },
        { value: 11, label: 'Listopad' },
        { value: 12, label: 'Grudzień' },
    ];

    // Generate years from 1900 to current year
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 2022 }, (_, i) => {
        const year = currentYear - i;
        return { value: year, label: year.toString() };
    });

    return <>
        <div className="d-flex flex-row">
            <FormElement className="flex-grow-1" headingText="Pozostałe ustawienia">
                {/* d-none because i do not support fully EUR/PLN conversion */}
                <Form.Group className="mb-3 w-50 d-none">
                    <Form.Label>Domyślna waluta</Form.Label>
                    <Form.Select 
                        value={otherSettings.defaultCurrency} 
                        onChange={(e) => handleOtherSettingsItemChange({ defaultCurrency: e.target.value as SupportedCurrency })}
                    >
                        <option value="PLN">PLN</option>
                        <option value="EUR">EUR</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3 w-50">
                    <Form.Label>Miesiąc rozpoczęcia</Form.Label>
                    <Form.Select 
                        value={otherSettings.startMonth}
                        onChange={(e) => handleOtherSettingsItemChange({ startMonth: Number(e.target.value) })}
                        disabled={!canEdit}
                    >
                        {months.map(month => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3 w-50">
                    <Form.Label>Rok rozpoczęcia</Form.Label>
                    <Form.Select 
                        value={otherSettings.startYear}
                        onChange={(e) => handleOtherSettingsItemChange({ startYear: Number(e.target.value) })}
                        disabled={!canEdit}
                    >
                        {years.map(year => (
                            <option key={year.value} value={year.value}>{year.label}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </FormElement>
        </div>
        <div className="d-flex flex-row justify-content-end">
            {canEdit && <Button disabled={!anyChanges} variant="primary" onClick={handleSubmitButtonClick}>Zapisz</Button>}
        </div>
    </>;
};
